// Wegfindung auf einem begehbaren Polygon (mit Löchern) über einen Sichtbarkeitsgraphen.
import type { Pt } from './types.ts';

export type Poly = Pt[];

export function toPoly(flat: number[]): Poly {
  const p: Poly = [];
  for (let i = 0; i < flat.length; i += 2) p.push({ x: flat[i], y: flat[i + 1] });
  return p;
}

export function pointInPoly(p: Pt, poly: Poly): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function cross(o: Pt, a: Pt, b: Pt) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** echte Kreuzung zweier Strecken (Berührung an Endpunkten zählt nicht) */
function segmentsCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
  return ((d1 > 1e-9 && d2 < -1e-9) || (d1 < -1e-9 && d2 > 1e-9)) && ((d3 > 1e-9 && d4 < -1e-9) || (d3 < -1e-9 && d4 > 1e-9));
}

export class NavMesh {
  outer: Poly;
  holes: Poly[];
  private nodes: Pt[] = [];

  constructor(outer: number[], holes: number[][] = []) {
    this.outer = toPoly(outer);
    this.holes = holes.map(toPoly);
    // Knoten: Polygonecken, leicht nach innen versetzt, damit Sichtlinien nicht an Kanten kleben
    const all = [this.outer, ...this.holes];
    for (const poly of all) {
      for (let i = 0; i < poly.length; i++) {
        const prev = poly[(i + poly.length - 1) % poly.length], cur = poly[i], next = poly[(i + 1) % poly.length];
        const nx = (cur.x - prev.x) / Math.hypot(cur.x - prev.x, cur.y - prev.y) - (next.x - cur.x) / Math.hypot(next.x - cur.x, next.y - cur.y);
        const ny = (cur.y - prev.y) / Math.hypot(cur.x - prev.x, cur.y - prev.y) - (next.y - cur.y) / Math.hypot(next.x - cur.x, next.y - cur.y);
        for (const s of [1, -1]) {
          const len = Math.hypot(nx, ny) || 1;
          const cand = { x: cur.x + (s * nx * 6) / len, y: cur.y + (s * ny * 6) / len };
          if (this.inside(cand)) {
            this.nodes.push(cand);
            break;
          }
        }
      }
    }
  }

  inside(p: Pt): boolean {
    return pointInPoly(p, this.outer) && !this.holes.some((h) => pointInPoly(p, h));
  }

  visible(a: Pt, b: Pt): boolean {
    for (const poly of [this.outer, ...this.holes]) {
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        if (segmentsCross(a, b, poly[i], poly[j])) return false;
      }
    }
    // Mittelpunkt prüfen (fängt Strecken entlang konkaver Ecken ab)
    const steps = Math.max(2, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 40));
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      if (!this.inside({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })) return false;
    }
    return true;
  }

  /** nächster begehbarer Punkt */
  clamp(p: Pt): Pt {
    if (this.inside(p)) return p;
    let best: Pt = this.nodes[0] ?? p, bestD = Infinity;
    for (const poly of [this.outer, ...this.holes]) {
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[j], b = poly[i];
        const dx = b.x - a.x, dy = b.y - a.y;
        const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1)));
        const q = { x: a.x + dx * t, y: a.y + dy * t };
        // ein Stück nach innen schieben
        for (const off of [4, 8, 16, 32]) {
          for (const [ox, oy] of [[0, off], [0, -off], [off, 0], [-off, 0], [off, off], [-off, -off], [off, -off], [-off, off]]) {
            const c = { x: q.x + ox, y: q.y + oy };
            const d = Math.hypot(c.x - p.x, c.y - p.y);
            if (d < bestD && this.inside(c)) {
              bestD = d;
              best = c;
            }
          }
        }
      }
    }
    return best;
  }

  /** kürzester Weg von a nach b (beide werden auf die Fläche geklemmt) */
  path(from: Pt, to: Pt): Pt[] {
    const a = this.clamp(from), b = this.clamp(to);
    if (this.visible(a, b)) return [a, b];
    const pts = [a, b, ...this.nodes];
    const n = pts.length;
    const dist = new Array(n).fill(Infinity);
    const prev = new Array(n).fill(-1);
    const done = new Array(n).fill(false);
    dist[0] = 0;
    for (;;) {
      let u = -1;
      for (let i = 0; i < n; i++) if (!done[i] && (u < 0 || dist[i] < dist[u])) u = i;
      if (u < 0 || dist[u] === Infinity) break;
      if (u === 1) break;
      done[u] = true;
      for (let v = 0; v < n; v++) {
        if (done[v] || v === u) continue;
        const d = dist[u] + Math.hypot(pts[u].x - pts[v].x, pts[u].y - pts[v].y);
        if (d < dist[v] && this.visible(pts[u], pts[v])) {
          dist[v] = d;
          prev[v] = u;
        }
      }
    }
    if (prev[1] < 0) return [a];
    const out: Pt[] = [];
    for (let i = 1; i >= 0; i = prev[i]) {
      out.unshift(pts[i]);
      if (i === 0) break;
    }
    return out;
  }
}
